import type { Signal } from '@preact/signals-react';
import { useSignal } from '@preact/signals-react';
import clsx from 'clsx';
import dot from 'dot-object';
import deepEquals from 'fast-deep-equal';
import { isEqual } from 'lodash';
import type { Key, KeyboardEvent, ReactNode, Ref } from 'react';
import { useCallback, useEffect, useId, useImperativeHandle, useMemo, useReducer, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import type {
  JsonDataWrapper,
  OdinDataRetrieval,
  OdinDataUpdate,
} from '../../../common/data-retrieval/data-retrieval-interfaces';
import type { OdinDataSender } from '../../../common/data-retrieval/odin-data-sender';
import { combineRefs } from '../../../common/functions/combine-refs';
import usePropertyHistory from '../../../common/hooks/use-property-history';
import { useSharedTestId } from '../../../common/hooks/use-shared-test-id';
import useStateRef from '../../../common/hooks/use-state-ref';
import useTestTag from '../../../common/hooks/use-test-tag';
import OverlayPanel from '../../common/overlay-panel/overlay-panel.component';
import { OverlayScrollDetectionType } from '../../common/overlay-panel/overlay-panel.props';
import RequiredIndicator from '../../common/required-indicator/required-indicator.component';
import { forceAssert } from '../../common-functions';
import type { TypeOrArray } from '../../common-interfaces';
import type { ContinuousScrollerFilter } from '../../continuous-scroller/continuous-scroller-types';
import type { DataFilterRef } from '../../data/data-filter/data-filter.component';
import type { DropDownOptionRendererRef } from '../../data/drop-down-option-renderer/drop-down-option-renderer.component';
import type {
  DynamicFieldComponentProps,
  DynamicFieldDataProps,
  DynamicFormFieldType,
} from '../../dynamic-form/dynamic-form.interfaces';
import { DataSearchLocation, DomTargetPosition } from '../../enums';
import type { JsonData, JsonDataItem } from '../../generic-types';
import EmptyFieldLabel from '../../labels/empty-field-label';
import ErrorLabel from '../../labels/error-label';
import OdinIcon, { OdinIconSize } from '../../odin-icon/odin-icon.component';
import TemplateOutlet from '../../template-outlet/template-outlet.component';
import type { DropDownState } from './drop-down.reducer';
import reducer, { DropDownActionType } from './drop-down.reducer';
import type { ActionSectionItem } from './drop-down-action-section.component';
import DropDownChip from './drop-down-chip.component';
import { isDropDownNoSelection, isDropDownResult } from './drop-down-common';
import type { DropDownFeatures } from './drop-down-features';
import DropDownOptionWrapper from './drop-down-option-wrapper.component';
import type {
  DropDownActionTemplateProps,
  DropDownActionTemplateType,
  DropDownNoSelection,
  DropDownOptionListTemplateType,
  DropDownOptionTemplateType,
  DropDownResult,
  DropDownSelectorTemplateProps,
  DropDownSelectorTemplateType,
  DropDownWrapperTemplateProps,
  DropDownWrapperTemplateType,
} from './drop-down-types';
import { DropDownSelectionType } from './drop-down-types';

export interface DropDownProps
  extends DynamicFieldComponentProps<TypeOrArray<DropDownResult> | DropDownNoSelection>,
    DynamicFieldDataProps {
  ref?: Ref<DropDownRef>;
  labelInsideDropDown?: boolean;
  allowClear?: boolean;
  allowSearch?: boolean;
  selectionType?: DropDownSelectionType;
  useCheckboxForMultiSelect?: boolean;
  useChips?: boolean;
  removeOnSelect?: boolean;
  /**
   * If true this will override the preloadData property since the data needs to be loaded to set the default value.
   */
  setDefaultValue?: boolean;
  /**
   * If true this will cause the data to load when the component is created instead of when the drop-down list is opened
   * (default: false).
   */
  preloadData?: boolean;
  multipleMessageCount?: number;
  multipleMessageText?: string;
  showAddNewOption?: boolean;
  showValueInSelector?: boolean;
  showArchivedOptions?: boolean;
  debounceTime?: number;
  disabled?: boolean;
  scrollDetection?: OverlayScrollDetectionType;
  actionItems?: Array<ActionSectionItem>;
  itemsToRemove?: Array<DropDownResult>;
  deselectValue?: DropDownResult | DropDownNoSelection;
  filterSignal?: Signal<Array<ContinuousScrollerFilter>>;
  filterLocation?: DataSearchLocation;
  closeIconSize?: OdinIconSize;
  dropDownIconSize?: OdinIconSize;
  onActionItemChanged?: (item: ActionSectionItem, features: DropDownFeatures) => void;
  className?: string;
  containerStyles?: string;
  labelStyles?: string;
  dropDownStyles?: string;
  selectorStyles?: string;
  clearButtonStyles?: string;
  dropDownButtonStyles?: string;
  optionListStyle?: string;
  optionHoverStyle?: string;
  optionSelectedStyle?: string;
  optionListPanelStyles?: string;
  actionTemplate?: DropDownActionTemplateType;
  dropDownWrapperTemplate?: DropDownWrapperTemplateType;
  selectorTemplate?: DropDownSelectorTemplateType;
  optionListTemplate?: DropDownOptionListTemplateType;
  optionTemplate?: DropDownOptionTemplateType;
  onDoubleClick?: (value: DropDownResult) => void;
  dropDownDataLoaded?: (page: number, data: JsonData) => void;
  shouldUseCreatePortal?: boolean;
  allowDuplicateCall?: boolean;
  onSelectorVisible?: (visible: boolean) => void;
  optionListPanelShouldMatchTargetWidth?: boolean;
  showDataOnSearch?: boolean;
  minimumSearchCharacters?: number;
}

export interface DropDownRef {
  focus: () => void;
  getDataOverwritePageCache: () => void;
  resetDropDownData: () => void;
}

const DropDown = (props: DropDownProps) => {
  const {
    ref,
    id,
    textField,
    valueField,
    archivedField,
    label,
    value,
    error,
    labelInsideDropDown = false,
    allowClear = true,
    allowSearch = true,
    selectionType = DropDownSelectionType.Single,
    useCheckboxForMultiSelect = true,
    useChips = false,
    removeOnSelect = false,
    setDefaultValue = false,
    preloadData = false,
    multipleMessageCount = 0,
    multipleMessageText,
    showAddNewOption = false,
    showValueInSelector = true,
    showArchivedOptions = false,
    debounceTime = 1000,
    disabled = false,
    scrollDetection = OverlayScrollDetectionType.Parent,
    pageSize = 50,
    actionItems,
    itemsToRemove,
    placeholder,
    deselectValue,
    filterSignal,
    filterLocation = DataSearchLocation.Local,
    data,
    readOnly = false,
    required,
    onActionItemChanged,
    onChange,
    onDoubleClick,
    closeIconSize = OdinIconSize.ExtraSmall,
    dropDownIconSize = OdinIconSize.Small,
    className,
    containerStyles,
    labelStyles = 'font-bold text-sm',
    dropDownStyles,
    selectorStyles,
    clearButtonStyles,
    dropDownButtonStyles,
    optionListStyle,
    optionHoverStyle,
    optionSelectedStyle,
    optionListPanelStyles,
    actionTemplate,
    dropDownWrapperTemplate,
    selectorTemplate,
    optionListTemplate,
    optionTemplate,
    dropDownDataLoaded,
    shouldUseCreatePortal = true,
    allowDuplicateCall,
    onSelectorVisible,
    optionListPanelShouldMatchTargetWidth = true,
    showDataOnSearch,
    minimumSearchCharacters,
  } = props;

  const initialState: DropDownState = {
    isListOpen: false,
    showClearButton: false,
    showPlaceholder: false,
    isPassedValue: false,
  };

  const [state, dispatcher] = useReducer(reducer, initialState);

  const [isListOpen, setIsListOpen, isListOpenRef] = useStateRef(false);

  const dropDownTargetRef = useRef<HTMLDivElement>(null);
  const dropDownButtonRef = useRef<HTMLButtonElement | null>(null);
  // set when the drop-down itself closes the list (selection, Escape or Tab) so the
  // trigger regains focus; click-away closes leave focus wherever the user put it
  const shouldRestoreFocus = useRef(false);
  const saveSender = useRef<OdinDataSender<JsonDataWrapper> | undefined>(undefined);
  const optionRendererRef = useRef<DropDownOptionRendererRef>(null);
  const filterInputRef = useRef<DataFilterRef>(null);
  const selectedValueRef = useRef<DropDownResult[]>(
    value ? (Array.isArray(value) ? value.filter(isDropDownResult) : isDropDownResult(value) ? [value] : []) : []
  );

  const _filterSignal = useSignal<Array<ContinuousScrollerFilter>>([]);
  const registerSignal = useSignal(false);
  const tagCreator = useTestTag(label || 'drop-down');
  const { t } = useTranslation();
  const triggerId = useId();

  usePropertyHistory((previousValue, currentValue) => {
    if (currentValue === false && previousValue === true && state.selectedValue) {
      // I have doing this, but the dispatcher was firing at the same time as the render. It would update the value, but
      // not cause a new render which would still leave the archived values as selected when they were turned back on.
      setTimeout(() => {
        const nonArchivedValues = state.selectedValue?.filter((value) => !value.archived);
        onChange?.(nonArchivedValues);
        dispatcher({
          type: DropDownActionType.SetSelectedValue,
          selectedValue: nonArchivedValues,
        });
      });
    }
  }, showArchivedOptions);

  // biome-ignore lint/correctness/useExhaustiveDependencies: Only sync when value prop changes, state.selectedValue check is to prevent redundant updates
  useEffect(() => {
    if (value && !isDropDownNoSelection(value) && !deepEquals(state.selectedValue, value)) {
      dispatcher({
        type: DropDownActionType.SetPassedValue,
        passedValue: value,
      });
    } else if (!value || isDropDownNoSelection(value)) {
      dispatcher({
        type: DropDownActionType.ClearSelectedValue,
      });
    }
  }, [value]);

  useEffect(() => {
    if (state.selectedValue) {
      if (state.selectedValue.length > 0) {
        dispatcher({
          type: DropDownActionType.UpdateSelectionIndicators,
          showPlaceholder: false,
          showClearButton:
            selectionType === DropDownSelectionType.Single
              ? !isEqual(state.selectedValue[0].originalData, deselectValue)
              : false,
        });
      } else {
        dispatcher({
          type: DropDownActionType.UpdateSelectionIndicators,
          showPlaceholder: true,
          showClearButton: false,
        });
      }
    } else {
      dispatcher({
        type: DropDownActionType.UpdateSelectionIndicators,
        showPlaceholder: true,
      });
    }
    selectedValueRef.current = state.selectedValue ?? [];
  }, [state.selectedValue, selectionType, deselectValue]);

  const getSubscriber = useCallback(
    (subscriber: OdinDataSender<JsonDataWrapper>, fieldType?: DynamicFormFieldType) => {
      if (data && !Array.isArray(data)) {
        saveSender.current = subscriber;
        data.getSubscriber?.(subscriber, fieldType);
      }
    },
    [data]
  );

  const dataRetriever = useMemo<(OdinDataRetrieval & OdinDataUpdate<JsonDataWrapper>) | undefined>(() => {
    if (data && !Array.isArray(data)) {
      return {
        getData: data.getData,
        getSubscriber,
        saveData: data.saveData,
      };
    }
  }, [getSubscriber]);

  const changeDropDownVisibility = () => {
    setIsListOpen(!isListOpenRef.current);
  };

  const combineSelectedItems = (items?: TypeOrArray<DropDownResult>) => {
    return [...(items ? (Array.isArray(items) ? items : [items]) : [])];
  };

  const onItemSelected = useCallback(
    (item?: TypeOrArray<DropDownResult>) => {
      if (selectionType === DropDownSelectionType.Single) {
        shouldRestoreFocus.current = true;
        setIsListOpen(false);
        onChange?.(item ? (Array.isArray(item) ? item[0] : item) : undefined);
      } else {
        onChange?.(combineSelectedItems(item));
      }
    },
    [selectionType, onChange, setIsListOpen]
  );

  const onOverlayPanelShow = () => {
    onSelectorVisible?.(true);
    registerSignal.value = true;

    filterInputRef.current?.focus();
  };

  const onOverlayHidden = useCallback(() => {
    setIsListOpen(false);
    onSelectorVisible?.(false);
    registerSignal.value = false;

    if (shouldRestoreFocus.current) {
      shouldRestoreFocus.current = false;
      dropDownButtonRef.current?.focus();
    }
  }, [onSelectorVisible, setIsListOpen]);

  const onClearClick = useCallback(() => {
    if (selectionType === DropDownSelectionType.Multiple) {
      dispatcher({
        type: DropDownActionType.SetPassedValue,
        passedValue: [],
      });

      onChange?.([]);
    } else {
      if (deselectValue) {
        if (isDropDownResult(deselectValue) || isDropDownNoSelection(deselectValue)) {
          const _deselectValue = isDropDownResult(deselectValue)
            ? ({
                text: dot.pick(textField, deselectValue),
                value: dot.pick(valueField, deselectValue),
                originalData: forceAssert<JsonDataItem>(deselectValue),
              } as DropDownResult)
            : deselectValue;

          if (!isDropDownNoSelection(_deselectValue)) {
            dispatcher({
              type: DropDownActionType.SetPassedValue,
              passedValue: _deselectValue,
            });
          } else {
            // if the deselect value is a drop-down no selection then the value should still be cleared
            dispatcher({
              type: DropDownActionType.ClearSelectedValue,
            });
          }
          onChange?.(_deselectValue);
        } else {
          dispatcher({
            type: DropDownActionType.ClearSelectedValue,
          });

          onChange?.({
            noSelectionValue: null,
          });
        }
      } else {
        dispatcher({
          type: DropDownActionType.ClearSelectedValue,
        });

        onChange?.({
          noSelectionValue: null,
        });
      }
    }
  }, [selectionType, deselectValue, onChange, textField, valueField]);

  const removeValue = (chipValue: DropDownResult) => {
    const newSelectedValues = state.selectedValue?.filter((value) => value.value !== chipValue.value);
    dispatcher({
      type: DropDownActionType.SetSelectedValue,
      selectedValue: newSelectedValues,
    });

    onChange?.(combineSelectedItems(newSelectedValues));
  };

  const onKeyPress = (event: KeyboardEvent<HTMLDivElement>) => {
    switch (event.code) {
      case 'Tab':
      case 'Escape':
        if (isListOpenRef.current) {
          shouldRestoreFocus.current = true;
          setIsListOpen(false);
        }
    }
  };

  const focus = () => {
    dropDownButtonRef.current?.focus();
  };

  const getDataOverwritePageCache = () => {
    optionRendererRef.current?.getDataOverwritePageCache();
  };

  const resetDropDownData = () => {
    optionRendererRef.current?.resetDropDownData();
  };

  useImperativeHandle(ref, () => ({
    focus,
    getDataOverwritePageCache,
    resetDropDownData,
  }));

  const getSelectorValue = () => {
    if (state.selectedValue && state.selectedValue.length > 0) {
      if (selectionType === DropDownSelectionType.Single) {
        return state.selectedValue[0].text;
      } else {
        if (!useChips) {
          return multipleMessageCount > 1 && state.selectedValue && state.selectedValue.length >= multipleMessageCount
            ? (multipleMessageText ?? t('multiple'))
            : state.selectedValue.map((value) => value.text).join(', ');
        }
      }
    }

    const space = '\u0020';
    return <>{space}</>;
  };

  const dropdownChips = () => {
    if (state.selectedValue) {
      return (
        <div className="flex flex-1 text-xs flex-wrap pt-1">
          {state.selectedValue.map((value) => (
            <DropDownChip
              key={value.value as Key}
              value={value}
              readOnly={readOnly}
              disabled={disabled}
              onRemove={removeValue}
            />
          ))}
        </div>
      );
    }
  };

  const selectorFinalStyles = clsx(
    'shrink leading-4 relative block w-full cursor-pointer overflow-hidden truncate',
    selectorStyles,
    {
      'whitespace-pre': !readOnly,
    }
  );

  const selectorTemplateProps: DropDownSelectorTemplateProps = {
    selectedValue: state.selectedValue,
    multipleMessageCount,
  };

  const selectorDefaultTemplate = useCallback(
    (_props: DropDownSelectorTemplateProps, children?: TypeOrArray<ReactNode>) => {
      return <>{children}</>;
    },
    []
  );

  const selectorElement = (
    <div ref={tagCreator('input')} className={selectorFinalStyles}>
      <TemplateOutlet props={selectorTemplateProps} template={selectorTemplate || selectorDefaultTemplate}>
        {showValueInSelector && getSelectorValue()}
      </TemplateOutlet>
    </div>
  );

  const dropDownButtonFinalStyles = clsx(
    'drop-down-action-icon-button order-2 ml-1 shrink border-gray-4 border-l px-1 focus:outline-hidden',
    {
      hidden: readOnly,
      'text-readonly': disabled,
    },
    dropDownButtonStyles
  );

  // order-1 puts the clear button visually before the trigger while the DOM order keeps the
  // trigger first, so keyboard users reach the drop-down before its clear action
  const clearButtonFinalStyles = clsx(
    'order-1 ml-1 flex aspect-square shrink cursor-pointer items-center justify-center rounded-sm p-1 text-gray-3 focus-visible:outline-2 focus-visible:outline-primary-2',
    {
      hidden: readOnly || disabled,
    },
    clearButtonStyles
  );

  const actionTemplateProps: DropDownActionTemplateProps = {
    allowClear,
    closeIconSize,
    dropDownIconSize,
    isOpen: isListOpen,
    clearSelection: onClearClick,
    changeDropDownVisibility,
  };

  const actionDefaultTemplate = useCallback(
    (_props: DropDownActionTemplateProps, children?: TypeOrArray<ReactNode>) => <>{children}</>,
    []
  );

  const actionElement = (
    <TemplateOutlet props={actionTemplateProps} template={actionTemplate || actionDefaultTemplate}>
      <button
        id={triggerId}
        className={dropDownButtonFinalStyles}
        onClick={changeDropDownVisibility}
        ref={combineRefs(dropDownButtonRef, tagCreator(isListOpen ? 'arrowups-icon' : 'arrowdowns-icon'))}
        type="button"
        aria-label={label}
        aria-expanded={isListOpen}
        aria-haspopup="listbox"
      >
        <OdinIcon size={dropDownIconSize} icon={isListOpen ? 'ChevronUp' : 'ChevronDown'} />
      </button>
      {allowClear && state.showClearButton && state.selectedValue && (
        <button
          type="button"
          className={clearButtonFinalStyles}
          onClick={onClearClick}
          aria-label={t('clear')}
          ref={tagCreator('close-icon')}
        >
          <OdinIcon icon="X" size={closeIconSize} />
        </button>
      )}
    </TemplateOutlet>
  );

  const onNewItemCreated = (value: JsonDataItem) => {
    saveSender.current?.sendData({ data: [value] });

    const selectedValue: DropDownResult = {
      value: value[valueField],
      text: value[textField] as string,
      archived: archivedField ? !!value[archivedField] : false,
      originalData: value,
    };

    if (selectionType == DropDownSelectionType.Multiple) {
      const updatedSelectedValue = [selectedValue, ...(selectedValueRef.current || [])];
      dispatcher({
        type: DropDownActionType.UpdateSelectedValue,
        selectedValue: updatedSelectedValue,
      });
      onItemSelected(updatedSelectedValue);
    } else {
      dispatcher({
        type: DropDownActionType.SetSelectedValue,
        selectedValue: [selectedValue],
      });
      onItemSelected(selectedValue);
    }
  };

  const containerFinalStyles = clsx(
    'flex w-full',
    { 'flex-col': label && !labelInsideDropDown, 'flex-row-reverse flex-wrap': !label },
    containerStyles
  );

  const dropDownFinalStyles = clsx(
    'w-full flex flex-1 flex-col rounded-sm border [&:has(.drop-down-action-icon-button:focus)]:border-primary-2!',
    {
      'border-primary-2!': isListOpen,
      'bg-mono-1 ps-2': !readOnly,
      'border-gray-4': !readOnly && !error,
      'border-transparent bg-transparent': readOnly,
      'border-error': error,
      'cursor-default pointer-events-none': disabled || readOnly,
      hidden: (readOnly || disabled) && (!state.selectedValue || selectionType === DropDownSelectionType.Multiple),
    },
    dropDownStyles,
    className
  );

  const dropDownWrapperTemplateProps: DropDownWrapperTemplateProps = {
    selectedValue: state.selectedValue,
    selectorElement,
    actionElement,
    dropDownFinalStyles,
    label,
    labelInsideDropDown,
    changeDropDownVisibility,
  };

  const dropDownWrapperDefaultTemplate = useCallback(
    (_props: DropDownWrapperTemplateProps, children?: TypeOrArray<ReactNode>) => <>{children}</>,
    []
  );

  const dropDownWrapperElement = (
    <TemplateOutlet
      props={dropDownWrapperTemplateProps}
      template={dropDownWrapperTemplate || dropDownWrapperDefaultTemplate}
    >
      <div ref={dropDownTargetRef} className={dropDownFinalStyles}>
        {label && labelInsideDropDown && (
          <div className="shrink text-xs" ref={tagCreator('label')}>
            {label}
          </div>
        )}
        <div
          className="flex w-full grow items-center justify-between cursor-pointer py-1.5 md:py-[2px]"
          onClick={changeDropDownVisibility}
        >
          {selectorElement}
          {actionElement}
        </div>
      </div>
    </TemplateOutlet>
  );

  const mountOnEnter = useMemo(() => (setDefaultValue ? false : !preloadData), [setDefaultValue, preloadData]);

  const isEmpty = useMemo(() => {
    return (
      !state.selectedValue?.length ||
      state.selectedValue?.[0]?.originalData?.value === '' ||
      state.selectedValue?.[0].value === ''
    );
  }, [state.selectedValue]);

  const renderComponent = () => {
    return (
      <div className={containerFinalStyles} onKeyDown={onKeyPress}>
        <div className="flex items-center">
          {label && !labelInsideDropDown && (
            <label className={labelStyles} htmlFor={triggerId} ref={tagCreator('label')}>
              {label}
            </label>
          )}
          <RequiredIndicator readOnly={readOnly} required={required} />
        </div>
        {readOnly && isEmpty ? <EmptyFieldLabel /> : dropDownWrapperElement}
        {error && <ErrorLabel className="w-full">{error}</ErrorLabel>}
        {useChips && dropdownChips()}
        <OverlayPanel
          visible={isListOpen}
          target={dropDownTargetRef.current}
          targetPosition={DomTargetPosition.BottomLeft}
          transitionDuration={0}
          shouldTargetCloseOverlay={false}
          shouldMatchTargetWidth={optionListPanelShouldMatchTargetWidth}
          shouldScrollCloseOverlay={true}
          shouldCheckZIndex={true}
          shouldRemainOnScreen={true}
          mountOnEnter={mountOnEnter}
          unmountOnExit={false}
          scrollDetection={scrollDetection}
          shown={onOverlayPanelShow}
          hidden={onOverlayHidden}
          shouldUseCreatePortal={shouldUseCreatePortal}
          className={optionListPanelStyles}
          isTargetWidthMinWidth={!optionListPanelShouldMatchTargetWidth}
        >
          <DropDownOptionWrapper
            ref={optionRendererRef}
            id={id || 0}
            textField={textField}
            valueField={valueField}
            archivedField={archivedField}
            data={dataRetriever || data}
            value={state.selectedValue}
            allowSearch={allowSearch}
            setDefaultValue={setDefaultValue}
            selectionType={selectionType}
            useCheckboxForMultiSelect={useCheckboxForMultiSelect}
            removeOnSelect={removeOnSelect}
            placeholder={placeholder ?? t('type-search')}
            filterSignal={filterSignal || _filterSignal}
            filterLocation={filterLocation}
            registerSignal={registerSignal}
            isListOpen={isListOpen}
            actionItems={actionItems}
            itemsToRemove={itemsToRemove}
            dispatcher={dispatcher}
            onChange={onItemSelected}
            optionListStyle={optionListStyle}
            optionHoverStyle={optionHoverStyle}
            optionSelectedStyle={optionSelectedStyle}
            optionTemplate={optionTemplate}
            optionListTemplate={optionListTemplate}
            onActionItemChanged={onActionItemChanged}
            onDoubleClick={onDoubleClick}
            dropDownDataLoaded={dropDownDataLoaded}
            pageSize={pageSize}
            debounceTime={debounceTime}
            showAddNewOption={showAddNewOption}
            allowDuplicateCall={allowDuplicateCall}
            filterInputRef={filterInputRef}
            onNewItemCreated={onNewItemCreated}
            showDataOnSearch={showDataOnSearch}
            minimumSearchCharacters={minimumSearchCharacters}
          />
        </OverlayPanel>
      </div>
    );
  };

  return useSharedTestId(`${label}-dropdown`, renderComponent());
};

export default DropDown;
